import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, FileText } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { Seo } from "../components/site/Seo";
import { SectionLabel } from "../components/site/SectionLabel";
import { ArticleBlocks } from "../components/site/ArticleBlocks";
import { ReportLeadForm, ReportDownloadButton } from "../components/site/ReportLeadForm";
import { getReport, REPORTS_GATED } from "../data/reports";
import NotFound from "./NotFound";

const SITE = "https://matrivon.com";
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function ReportLanding({ slug }) {
  const r = getReport(slug);
  if (!r) return <NotFound />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Report",
      name: r.title,
      headline: r.title,
      description: r.metaDescription,
      datePublished: r.published,
      dateModified: r.reviewed,
      about: "GCC polyurethane market",
      keywords: (r.keywords || []).join(", "),
      author: { "@type": "Organization", name: "MATRIVON", url: SITE },
      publisher: { "@type": "Organization", name: "MATRIVON", url: SITE },
      url: `${SITE}${r.path}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Market Intelligence", item: `${SITE}/market-intelligence` },
        { "@type": "ListItem", position: 3, name: r.title, item: `${SITE}${r.path}` },
      ],
    },
  ];

  return (
    <div>
      <Seo title={r.seoTitle} description={r.metaDescription} path={r.path} jsonLd={jsonLd} />
      <PageHero
        variant="dark"
        label={`${r.category} · ${r.period}`}
        title={r.title}
        intro={r.subtitle}
        crumbs={[{ label: "Market Intelligence", to: "/market-intelligence" }, { label: r.period }]}
      />

      {/* Headline stats + intro */}
      <section className="border-b border-brand-line bg-brand-bg">
        <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <p className="max-w-3xl text-[17px] leading-relaxed text-brand-ink/85">{r.intro}</p>
          <div className="mt-8 grid gap-px border border-brand-line bg-brand-line sm:grid-cols-2 lg:grid-cols-4" data-testid="report-headline-stats">
            {r.headlineStats.map((s) => (
              <div key={s.label} className="bg-white p-6">
                <div className="font-mono text-2xl font-bold text-brand-blue lg:text-3xl">{s.value}</div>
                <div className="mt-2 text-sm leading-snug text-brand-muted">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[2fr_1fr]">
          <article className="min-w-0">
            <div className="mb-8 flex flex-wrap gap-4 border-b border-brand-line pb-6 text-xs text-brand-muted">
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> Published: {fmtDate(r.published)}</span>
              <span>Region: {r.region}</span>
              <span>{r.readMins} min read</span>
            </div>
            <ArticleBlocks blocks={r.blocks} />

            {/* Internal links */}
            <div className="mt-12 border border-brand-blue/20 bg-brand-blue/5 p-6">
              <h2 className="font-heading text-xl font-bold text-brand-ink">Related MATRIVON services</h2>
              <ul className="mt-4 space-y-2.5">
                {r.related.map((rel) => (
                  <li key={rel.to}>
                    <Link to={rel.to} className="group inline-flex items-start gap-2 text-[15px] font-medium text-brand-blue hover:gap-2.5">
                      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-brand-red" /> {rel.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </article>

          <aside className="space-y-8 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-line bg-brand-ink p-6" id="download" data-testid="report-download-card">
              <SectionLabel light>Full report</SectionLabel>
              <h2 className="font-heading text-lg font-bold text-white">Download the Full Report — {r.title}</h2>
              <div className="mt-4">
                {REPORTS_GATED ? (
                  <ReportLeadForm reportSlug={r.slug} sourcePage={r.path} pdfFilename={r.pdfFilename} />
                ) : (
                  <ReportDownloadButton reportSlug={r.slug} pdfFilename={r.pdfFilename} />
                )}
              </div>
            </div>

            <div className="border border-brand-line bg-white p-6">
              <SectionLabel>Who this report is for</SectionLabel>
              <ul className="mt-3 space-y-2 text-sm text-brand-ink/85">
                {r.forWhom.map((w) => (
                  <li key={w} className="flex gap-2">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /> {w}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* Secondary commercial CTA */}
      <section className="relative overflow-hidden bg-brand-ink text-white">
        <div className="absolute inset-0 grid-paper-dark opacity-40" />
        <div className="relative mx-auto max-w-5xl px-5 py-20 text-center lg:px-8">
          <h2 className="mx-auto max-w-3xl font-heading text-3xl font-bold tracking-tight lg:text-4xl">
            Need a deeper market or competitor analysis for your business?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/70">
            MATRIVON prepares independent, technically grounded market and competitor intelligence tailored to your products, regions and commercial questions.
          </p>
          <div className="mt-9">
            <Link
              to="/contact"
              data-testid="report-custom-cta"
              className="inline-flex items-center gap-2 bg-brand-red px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:brightness-110"
            >
              Discuss a Custom Market Intelligence Project <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
